import { useRef, useState, useEffect } from 'react';
import { Tooltip } from './Tooltip';
import { useNetlifyLogs } from '~/lib/hooks/useNetlifyLogs';
import { classNames } from '~/utils/classNames';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface NetlifyLogsButtonProps {
  deployedId: string | null;
  accessToken: string | null | undefined;
  deployStatus?: string;
  autoExpand?: boolean;
  adminUrl?: string | null;
}

export const NetlifyLogsButton = ({
  deployedId,
  accessToken,
  deployStatus,
  autoExpand = false,
}: NetlifyLogsButtonProps) => {
  const { t } = useTranslation();
  const logsRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(autoExpand);

  const {
    logs,
    isConnecting: isConnectingToLogs,
    isViewingLogs,
    connectToLogs,
    safeConnectToLogs,
    formatTimestamp,
    formatLogMessage,
    setIsViewingLogs,
  } = useNetlifyLogs(deployedId, accessToken);

  // Auto-expand logs when autoExpand prop changes
  useEffect(() => {
    if (autoExpand && deployStatus !== 'error') {
      console.log('[NetlifyLogsButton] Auto-expanding logs, deployStatus:', deployStatus);
      setIsExpanded(true);
      setIsViewingLogs(true);
    }
  }, [autoExpand, deployStatus, setIsViewingLogs]);

  // Scroll to bottom when new logs arrive
  useEffect(() => {
    if (logsRef.current && logs.length > 0) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [logs]);

  // Toggle logs visibility
  const toggleLogs = () => {
    console.log('[NetlifyLogsButton] Toggling logs visibility, current state:', {
      isViewingLogs,
      isExpanded,
      connecting: isConnectingToLogs,
      existingLogs: logs.length,
    });

    // Only connect if not already viewing and we're expanding
    if (!isViewingLogs && !isExpanded) {
      // Check if we already have logs or are already connecting before attempting to connect
      if (logs.length === 0 && !isConnectingToLogs) {
        console.log('[NetlifyLogsButton] No logs available, initiating connection');
        safeConnectToLogs();
      } else {
        // Just set viewing state if we already have logs or are connecting
        console.log('[NetlifyLogsButton] Logs already available or connecting, just setting viewing state');
        setIsViewingLogs(true);
      }
    }

    setIsExpanded(!isExpanded);
  };

  // Render logs content
  const renderLogsContent = () => (
    <div
      ref={logsRef}
      className="h-[150px] overflow-y-auto p-3 rounded-md border border-dualite-border-primary 
        font-mono text-xs text-dualite-text-primary whitespace-pre-wrap"
    >
      {isConnectingToLogs && logs.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="i-ph:circle-notch w-4 h-4 animate-spin mr-2 text-dualite-text-modal-secondary" />
          <span className="text-[#e7e7e7]/70">{t('modals.deploy.logs.connecting')}</span>
        </div>
      ) : logs.length === 0 ? (
        <div className="flex items-center justify-center h-full text-dualite-text-modal-secondary">
          {t('modals.deploy.logs.noLogs')}
        </div>
      ) : (
        logs.map((log, index) => (
          <div key={index} className="mb-1">
            <span className="text-dualite-text-modal-secondary mr-2">[{formatTimestamp(log.timestamp)}]</span> <br />
            <span
              className={`
                ${log.level === 'error' ? 'text-dualite-text-modal-primary underline decoration-dualite-text-modal-primary/30' : ''}
                ${log.level === 'warn' ? 'text-dualite-text-modal-secondary/90' : ''}
                ${log.level === 'info' ? 'text-dualite-text-modal-primary' : ''}
                ${log.level === 'success' ? 'text-dualite-text-modal-primary font-medium' : ''}
              `}
              dangerouslySetInnerHTML={{ __html: formatLogMessage(log.message) }}
            />
          </div>
        ))
      )}
    </div>
  );

  // If no deployment is in progress, return null
  if (!deployedId || !accessToken) {
    return null;
  }

  // Show only the header with toggle and logs when expanded
  return (
    <div>
      {/* Deployment Logs Header with Toggle */}
      <div className="flex items-center justify-between bg-dualite-background-tertiary">
        <div className="flex items-center gap-2 cursor-pointer py-1" onClick={toggleLogs}>
          <div className="i-ph:terminal w-4 h-4 text-dualite-text-secondary" />
          <h3 className="text-xs text-dualite-text-secondary">{t('modals.deploy.logs.advanced')}</h3>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip content={isExpanded ? t('modals.deploy.logs.collapse') : t('modals.deploy.logs.expand')}>
            <button
              onClick={toggleLogs}
              className="p-1.5 text-dualite-text-modal-secondary hover:text-dualite-text-modal-primary bg-dualite-background-button/40 
                hover:bg-dualite-background-button/50 rounded-md transition-colors"
            >
              <div
                className={classNames(
                  'i-ph:caret-down-bold w-3.5 h-3.5 transition-transform',
                  isExpanded ? 'rotate-180' : '',
                )}
              />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Expanded Logs Panel */}
      {isExpanded && renderLogsContent()}
    </div>
  );
};
